import { useCallback, useMemo, useRef } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useUserData } from '@nhost/react'
import { useArticleVisibility } from './articleVisibility'
import { ADVANCE_CURRENT_CHAPTER, GET_PROGRESS, MARK_ARTICLE, SET_COMPLETED_CHAPTERS } from './api'

/**
 * Lernfortschritt auf zwei Ebenen.
 *
 * Artikel liegen als Zeilen in `article_progress` - eine Zeile pro (user, article),
 * das Unique-Constraint dedupliziert in der Datenbank statt im Client. Mehrfaches
 * Markieren aus zwei Tabs oder aus zwei Triggern gleichzeitig ist damit folgenlos.
 *
 * Kapitel bleiben in `user_profiles.completed_chapters` (jsonb). Das Feld haengt an
 * fuenf UI-Stellen und bleibt der denormalisierte Cache. Gesetzt wird es
 * ausschliesslich abgeleitet - wenn alle Artikel eines Kapitels erledigt sind. Es gibt
 * keinen Knopf mehr, der ein Kapitel fuer abgeschlossen erklaert, also kann der
 * Kapitelstatus auch nicht mehr mehr behaupten als die Artikelzeilen hergeben.
 * Geschrieben wird mit _set statt _append, damit die Liste ein Set bleibt.
 *
 * Die Dokumente liegen in `features/content/api.ts`, wie die übrigen
 * Content-Operationen.
 */

export type ChapterProgress = {
  done: number
  total: number
  /** 0..1, bei einem Kapitel ohne Artikel 0. */
  ratio: number
  /** Aus `completed_chapters` - kann true sein, waehrend done < total ist. */
  completed: boolean
}

export function useProgress() {
  const user = useUserData()
  const userId = user?.id

  const { data, loading, refetch } = useQuery(GET_PROGRESS, {
    variables: { userId: userId ?? '' },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
    // Ohne Fortschritt fehlen nur die Haken; der Hook hat keine eigene Oberfläche, auf
    // der er eine Meldung zeigen könnte.
    onError: (err) => console.error('Failed to load learning progress:', err),
  })

  const [markArticleMutation] = useMutation(MARK_ARTICLE)
  const [setChaptersMutation] = useMutation(SET_COMPLETED_CHAPTERS)
  const [advanceChapterMutation] = useMutation(ADVANCE_CURRENT_CHAPTER)

  const completedArticles = useMemo(
    () => new Set((data?.article_progress ?? []).map((row) => row.article_slug)),
    [data?.article_progress]
  )

  const completedChapters = useMemo(() => {
    // `completed_chapters` ist eine jsonb-Spalte und kommt deshalb als `unknown` aus dem
    // Codegen. Gespeichert wird dort immer eine Liste von Kapitel-Slugs.
    const raw = data?.user_profiles?.[0]?.completed_chapters
    return Array.isArray(raw) ? (raw as string[]) : []
  }, [data?.user_profiles])

  const isArticleDone = useCallback(
    (slug: string) => completedArticles.has(slug),
    [completedArticles]
  )

  const isChapterDone = useCallback(
    (slug: string) => completedChapters.includes(slug),
    [completedChapters]
  )

  /**
   * Ist das Kapitel durch? Zuerst die Artikelzeilen, dann der gespeicherte Cache.
   *
   * Die Reihenfolge ist der Punkt: `completed_chapters` wird nur geschrieben, solange
   * eine Leseansicht offen ist. Wer den letzten Artikel beendet und die Ansicht
   * schliesst, bevor der Schreibvorgang durch ist, haette sonst dauerhaft keinen Haken -
   * die Uebersicht zeigte "3 von 3" und die Kachel trotzdem nichts. Am Bestand
   * abgelesen kann der Haken nicht verloren gehen.
   *
   * Der Cache bleibt als Rueckfall fuer Aufrufer ohne Artikelliste.
   */
  const isChapterComplete = useCallback(
    (chapterSlug: string, articleSlugs: string[]) =>
      (articleSlugs.length > 0 && articleSlugs.every((slug) => completedArticles.has(slug))) ||
      completedChapters.includes(chapterSlug),
    [completedArticles, completedChapters]
  )

  const chapterProgress = useCallback(
    (chapterSlug: string, articleSlugs: string[]): ChapterProgress => {
      const done = articleSlugs.filter((slug) => completedArticles.has(slug)).length
      return {
        done,
        total: articleSlugs.length,
        ratio: articleSlugs.length > 0 ? done / articleSlugs.length : 0,
        completed: completedChapters.includes(chapterSlug),
      }
    },
    [completedArticles, completedChapters]
  )

  // Verhindert doppelte Requests, wenn zwei Trigger (Audio-Ende und Lese-Sentinel)
  // im selben Moment fuer denselben Artikel feuern. Die Datenbank wuerde beides
  // korrekt verarbeiten, aber der zweite Request waere reine Verschwendung.
  const inFlight = useRef(new Set<string>())

  /**
   * Schliesst ein Kapitel ab und rueckt optional den Wiedereinstiegspunkt vor.
   * Aufgerufen wird das aus der Ableitung heraus, nicht von einem Knopf.
   */
  const markChapterComplete = useCallback(
    async (chapterSlug: string, advanceTo?: string | null) => {
      if (!userId) return
      if (completedChapters.includes(chapterSlug)) return
      if (inFlight.current.has(chapterSlug)) return

      inFlight.current.add(chapterSlug)
      const next = Array.from(new Set([...completedChapters, chapterSlug]))
      try {
        await setChaptersMutation({ variables: { userId, chapters: next } })
        if (advanceTo) {
          await advanceChapterMutation({ variables: { userId, chapterSlug: advanceTo } })
        }
        await refetch()
      } catch (err) {
        console.error('Failed to mark chapter completed:', err)
      } finally {
        inFlight.current.delete(chapterSlug)
      }
    },
    [userId, completedChapters, setChaptersMutation, advanceChapterMutation, refetch]
  )

  /** Traegt einen Artikel als gelesen ein. Ob damit ein Kapitel faellt, entscheidet
   *  die Ableitung beim Aufrufer - er allein weiss, welche Artikel er anzeigt. */
  const markArticleComplete = useCallback(
    async (articleSlug: string, chapterSlug: string) => {
      if (!userId || !articleSlug || !chapterSlug) return
      if (completedArticles.has(articleSlug)) return
      if (inFlight.current.has(articleSlug)) return

      inFlight.current.add(articleSlug)
      try {
        await markArticleMutation({ variables: { userId, articleSlug, chapterSlug } })
        await refetch()
      } catch (err) {
        console.error('Failed to mark article completed:', err)
      } finally {
        inFlight.current.delete(articleSlug)
      }
    },
    [userId, completedArticles, markArticleMutation, refetch]
  )

  return {
    loading,
    completedArticles,
    completedChapters,
    isArticleDone,
    isChapterDone,
    isChapterComplete,
    chapterProgress,
    markArticleComplete,
    markChapterComplete,
    refetch,
  }
}

/**
 * Fuer die Kapitellisten (Kursstart, Entdecken, Meine Titel, Slider, Uebersicht):
 * liefert die Slugs der abgeschlossenen Kapitel, abgelesen an den Artikelzeilen.
 *
 * Die Sichtbarkeit wird mitgerechnet - ein vom Fragebogen ausgeblendeter Artikel darf
 * ein Kapitel nicht offen halten, denn zu sehen bekommt ihn diese Nutzerin nie.
 */
export function useCompletedChapterSlugs(
  chapters: { slug: string; articles?: { slug: string }[] | null }[]
): string[] {
  const { isChapterComplete } = useProgress()
  const { isArticleVisible } = useArticleVisibility()

  return useMemo(
    () =>
      chapters
        .filter((chapter) =>
          isChapterComplete(
            chapter.slug,
            (chapter.articles ?? []).map((a) => a.slug).filter(isArticleVisible)
          )
        )
        .map((chapter) => chapter.slug),
    [chapters, isChapterComplete, isArticleVisible]
  )
}
